import { type HTMLAttributes, type ReactNode } from 'react';

import { XIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';
import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

import { SEARCH_TOKEN_CLOSE_BTN } from '~/constants/testIDs';
import { preventDefaultMouseEvent } from '~/utils/ClickUtils';

const searchTokenVariants = tailwindVariants({
  base: 'flex max-w-[120px] items-center rounded-6 py-0.5 pr-1 pl-2',
  variants: {
    color: {
      grey: 'ads:bg-ads-gray-4 bg-gray-4 ads:text-secondary text-gray-11',
      blue: 'ads:bg-primary-subtle bg-blue-1 ads:text-accent text-blue-11',
    },
  },
});

type SearchTokenVariant = VariantProps<typeof searchTokenVariants>['color'];

type SearchTokenProps = {
  tokenName?: string;
  onCloseClick?: () => void;
  color: SearchTokenVariant;
  icon?: ReactNode;
  tooltipText?: string;
  testId?: string;
} & Pick<HTMLAttributes<HTMLDivElement>, 'onMouseEnter' | 'onMouseLeave'>;

export const SearchToken = ({
  icon,
  tokenName,
  onCloseClick,
  color,
  tooltipText,
  onMouseLeave,
  onMouseEnter,
  testId,
}: SearchTokenProps) => (
  // biome-ignore lint/a11y/noStaticElementInteractions: PLA-1724 — pre-existing debt, not introduced by the icon migration
  <div
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    data-testid={testId}
    className={searchTokenVariants({ color })}
  >
    {icon}
    {!!tokenName && (
      <Tooltip label={tooltipText || tokenName} side="top">
        <p className="mr-1 truncate font-medium text-14">{tokenName}</p>
      </Tooltip>
    )}

    {!!onCloseClick && (
      <IconButton
        adsIcon
        label="Remove from search"
        data-testid={SEARCH_TOKEN_CLOSE_BTN}
        icon={XIcon}
        onMouseDown={preventDefaultMouseEvent}
        onClick={onCloseClick}
        appearance={color === 'grey' ? 'filled' : 'ghost'}
        color={color === 'grey' ? 'grey' : 'blue'}
        size="extra-small"
        // biome-ignore lint/plugin: PLA-1724 — pre-existing important size override, not introduced by the color pairing
        className="!h-3 !w-3"
      />
    )}
  </div>
);
